<template>
  <div class="mb-4 flex justify-between">
    <div>
      <h2
        class="shrink-0 text-lg font-medium text-background-fg/87"
      >
        Recent Transactions
      </h2>
      <p
        v-if="!!timeSinceLastTransaction"
        class="text-sm text-neutral-500"
      >
        last transaction {{ timeSinceLastTransaction }} ago
      </p>
    </div>
    <ButtonWithIcon
      v-if="data != null"
      @click="refreshData"
    >
      <IconRefresh />
    </ButtonWithIcon>
  </div>

  <QueryLoading v-if="isLoading" />
  <QueryError
    v-else-if="isError"
    class="min-h-48"
    :show-retry="true"
    @retry="refetch"
  >
    Failed to retrieve recent transactions
  </QueryError>
  <div v-else class="relative min-h-48">
    <BaseTable
      :rows="data ?? []"
      :columns="columnDefinitions"
    />
  </div>
</template>

<script setup lang="ts">
import * as DateFns from "date-fns"
import type { TableColumn } from "@/components/BaseTable.vue"
import type { MinimalTransaction } from "@/services/transactions/listLastTenTransactions"

const props = defineProps<{
  source: PlatformStatsSources
}>()

const { data, refetch, isLoading, isError } =
  props.source === "tpplus"
    ? useLastTenTransactionsTpplus()
    : useLastTenTransactionsBeepaidGo()

const now = useNow()

const refreshData = () => refetch()

const formatDate = (date: Date, reference: Date) => {
  const distance = DateFns.formatDistance(date, reference, {
    includeSeconds: true,
  })
  return `${distance} ago`
}

const timeSinceLastTransaction = computed(() => {
  if (!data.value || data.value.length === 0) {
    return undefined
  }

  const lastTransaction = data.value[0].createdAt

  return DateFns.formatDistance(now.value, lastTransaction)
})

const columnDefinitions: Array<
  TableColumn<
    MinimalTransaction,
    "clientname" | "createdAt" | "amount" | "source"
  >
> = [
  {
    id: "clientname",
    header: "Client Name",
    getCellValue: (row) => row.name,
  },
  {
    id: "createdAt",
    header: "Created",
    getCellValue: (row) =>
      formatDate(row.createdAt, now.value),
  },
  {
    id: "amount",
    header: "Amount",
    getCellValue: (row) =>
      formatToCurrencyString(row.amountGross, row.currency),
  },
  {
    id: "source",
    header: "Source",
    getCellValue: (row) => formatSource(row),
  },
]

const formatSource = ({
  source,
  version,
}: MinimalTransaction) => {
  switch (source) {
    case "beepaid_go":
      return "beepaid Go"
    case "tpplus": {
      if (version === "web") return "tp+ web"

      return `tp+ v${version || "2"}`
    }
  }
}
</script>

<style lang="postcss">
.header {
  @apply whitespace-nowrap px-2 py-3.5 text-left text-sm font-semibold text-neutral-500;
}
.row {
  @apply whitespace-nowrap p-2 text-sm font-medium text-neutral-500;
}
</style>
